<template>
  <BaseCard tone="soft" class="dashboard-header">
    <div class="dashboard-header__layout">
      <div class="dashboard-header__top">
        <div class="dashboard-header__top-main">
          <div class="dashboard-header__avatar">{{ initials }}</div>

          <div class="dashboard-header__name-wrap">
            <h1>{{ displayName }}</h1>
          </div>
        </div>

        <router-link
          class="dashboard-header__top-arrow"
          to="/profile"
          aria-label="Перейти к редактированию профиля"
          title="Редактировать профиль"
        >
          <BaseIcon name="arrow-right" />
        </router-link>
      </div>

      <div class="dashboard-header__stats-row">
        <div class="dashboard-header__points">
          <span class="dashboard-header__points-icon">
            <BaseIcon name="ticket" />
          </span>

          <div class="dashboard-header__points-copy">
            <span class="dashboard-header__card-label">Баллы клуба</span>
            <strong class="dashboard-header__card-value">{{ bonusLabel }}</strong>
          </div>
        </div>

        <div class="dashboard-header__balance-card">
          <span class="dashboard-header__balance-icon">
            <BaseIcon name="wallet" />
          </span>

          <div class="dashboard-header__balance-copy">
            <span class="dashboard-header__card-label">Баланс</span>
            <strong class="dashboard-header__card-value">{{ balanceText }}</strong>
          </div>
        </div>

        <button
          type="button"
          class="dashboard-header__topup-card"
          @click="mockTopUp"
          aria-label="Пополнение счёта скоро появится"
          title="Пополнение счёта скоро появится"
        >
          <span class="dashboard-header__topup-icon" aria-hidden="true">
           <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><!-- Icon from Huge Icons by Hugeicons - undefined --><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"><path d="M11 21.5h4c2.828 0 4.243 0 5.121-.879C21 19.743 21 18.328 21 15.5v-1c0-2.828 0-4.243-.879-5.121C19.243 8.5 17.828 8.5 15 8.5H3v5m12-5.002V4.11a1.61 1.61 0 0 0-2.347-1.432L3.763 7.25A1.4 1.4 0 0 0 3 8.5"/><path d="M17.5 15.5a.5.5 0 0 0 0-1m0 1a.5.5 0 0 1 0-1m0 1v-1M10 18H6.5m0 0H3m3.5 0v-3.5m0 3.5v3.5"/></g></svg>
          </span>
        </button>
      </div>

      <button
        type="button"
        class="dashboard-header__club"
        :class="{ 'dashboard-header__club--collapsed': isDetailsCollapsed }"
        @click="toggleDetails"
      >
        <span class="dashboard-header__club-name">{{ clubName }}</span>

        <span class="dashboard-header__club-side">
          <span
            v-if="isDetailsCollapsed"
            class="dashboard-header__club-booking-status"
          >
            {{ collapsedBookingStatus }}
          </span>

          <span class="dashboard-header__club-arrow" aria-hidden="true"></span>
        </span>
      </button>

      <div
        class="dashboard-header__details"
        :class="{ 'dashboard-header__details--collapsed': isDetailsCollapsed }"
      >
        <div class="dashboard-header__details-inner">
          <div class="dashboard-header__details-content">
            <div class="dashboard-header__status-row">
              <span class="dashboard-header__status-label">Твой статус в клубе</span>
              <strong class="dashboard-header__status-value">{{ membership }}</strong>
            </div>

            <div class="dashboard-header__booking-row">
              <router-link
                v-if="nextBooking"
                class="dashboard-header__next-booking dashboard-header__next-booking--link"
                :to="nextBookingRoute"
              >
                <div class="dashboard-header__info-icon dashboard-header__info-icon--booking">
                  <BaseIcon name="calendar" />
                </div>

                <div class="dashboard-header__booking-copy">
                  <span class="dashboard-header__card-label">Следующая бронь</span>
                  <strong class="dashboard-header__booking-title">{{ nextBookingTitle }}</strong>
                  <span class="dashboard-header__booking-period">{{ nextBookingPeriod }}</span>
                </div>
              </router-link>

              <div v-else class="dashboard-header__next-booking">
                <div class="dashboard-header__info-icon dashboard-header__info-icon--booking">
                  <BaseIcon name="calendar" />
                </div>

                <div class="dashboard-header__booking-copy">
                  <span class="dashboard-header__card-label">Следующая бронь</span>
                  <strong class="dashboard-header__booking-title">Нет активных</strong>
                </div>
              </div>

              <router-link
                class="dashboard-header__bookings-link"
                to="/requests"
                aria-label="Открыть список броней"
                title="Список броней"
              >
                <BaseIcon name="ticket" />
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <router-link class="dashboard-header__book-button" to="/book">
        Забронировать
      </router-link>
    </div>
  </BaseCard>
</template>

<script>
import BaseCard from '../ui/BaseCard.vue';
import BaseIcon from '../ui/BaseIcon.vue';
import {
  formatPrice,
  formatTime,
  initialsFromName,
  userDisplayName,
} from '../../utils/formatters';
import { getUpcomingBookings, sortBookings } from '../../utils/bookingState';

export default {
  name: 'DashboardHeader',
  components: {
    BaseCard,
    BaseIcon,
  },
  props: {
    user: {
      type: Object,
      default: null,
    },
    notifications: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isDetailsCollapsed: true,
    };
  },
  computed: {
    currentClub() {
      return this.$store.getters['clubs/currentClub'];
    },
    orders() {
      return this.$store.getters['orders/items'] || [];
    },
    bookingTimeZone() {
      return this.currentClub?.timezone || this.currentClub?.club_timezone;
    },
    bookingGroups() {
      return sortBookings(this.orders, this.bookingTimeZone);
    },
    upcomingBookings() {
      return getUpcomingBookings(this.orders, this.bookingTimeZone);
    },
    nextBooking() {
      return this.upcomingBookings[0] || null;
    },
    nextBookingRoute() {
      if (!this.nextBooking?.order_number) {
        return '/requests';
      }

      return `/requests/${this.nextBooking.order_number}`;
    },
    displayName() {
      return userDisplayName(this.user);
    },
    initials() {
      return initialsFromName(this.displayName);
    },
    clubName() {
      return this.currentClub?.name || 'Клуб не выбран';
    },
    bonusLabel() {
      return `${Number(this.user?.bonus_balance || 0).toLocaleString('ru-RU')} LB`;
    },
    balanceText() {
      return formatPrice(this.user?.balance || 0);
    },
    membership() {
      const directStatus =
        this.user?.club_status ||
        this.user?.membership ||
        this.user?.member_status ||
        this.user?.rank ||
        this.user?.loyalty_status;

      if (directStatus) {
        return String(directStatus);
      }

      if (Number(this.user?.is_admin || 0) === 1) {
        return 'Администратор';
      }

      const points = Number(this.user?.bonus_balance || 0);

      if (points >= 500) return 'Event Horizon';
      if (points >= 150) return 'Core';

      return 'Стандарт';
    },
    nextBookingTitle() {
      if (!this.nextBooking) return '';

      return (
        this.nextBooking.seat_label ||
        this.nextBooking.zone_name ||
        this.nextBooking.tariff_name ||
        this.nextBooking.club_name ||
        'Ближайшая бронь'
      );
    },
    nextBookingPeriod() {
      if (!this.nextBooking?.start_time) return '';

      const from = formatTime(this.nextBooking.start_time);
      const to = this.nextBooking.end_time ? formatTime(this.nextBooking.end_time) : '';

      return to ? `${from} — ${to}` : `с ${from}`;
    },
    collapsedBookingStatus() {
      if (!this.nextBooking?.start_time) {
        return 'Нет активных';
      }

      const from = formatTime(this.nextBooking.start_time);
      const to = this.nextBooking.end_time ? formatTime(this.nextBooking.end_time) : '';

      return to ? `${from} — ${to}` : from;
    },
  },
  methods: {
    toggleDetails() {
      this.isDetailsCollapsed = !this.isDetailsCollapsed;
    },
    mockTopUp() {
      // пока заглушка
    },
  },
};
</script>

<style scoped src="./DashboardHeader.css"></style>